import { request, requestNoContent, requireBoolean, requireString, send } from '@/lib/api'
import type { components } from '@/lib/openapi.generated'
import type { AssistantContextItem } from '@/lib/assistant-context.generated'
import {
  decodeAssistantBrowserCommand,
  type AssistantBrowserCommand,
} from '@/lib/assistant-browser-commands.generated'

import type { AssistantTier } from './assistant-tier-api'

export type AssistantEvent =
  | { type: 'session'; sessionId: string }
  | { type: 'message'; text: string }
  | { type: 'pending-proposal'; id: string; details: unknown; expiresAt: string }
  | { type: 'capability-error'; tool: string; reason: string }
  | { type: 'ui-command'; command: AssistantBrowserCommand }
  | { type: 'file'; name: string; contentType: string; content: string }
  | { type: 'error'; code: string }
  | { type: 'done' }

export type AssistantProposalOutcome =
  | 'pending'
  | 'executing'
  | 'executed'
  | 'rejected'
  | 'stale'
  | 'expired'
  | 'failed'
  | 'forbidden'

export type AssistantCapabilityError = { tool: string; reason: string }

export type AssistantStoredProposal = {
  id: string
  details: unknown
  expiresAt: string
  outcome: AssistantProposalOutcome
}

export type AssistantStoredTurn = {
  userText: string
  assistantText: string
  capabilityErrors: AssistantCapabilityError[]
  proposals: AssistantStoredProposal[]
  errorCode: string | null
}

export type AssistantSession = {
  sessionId: string
  turns: AssistantStoredTurn[]
  activeTurn?: boolean
}

export type AssistantImage = components['schemas']['AssistantImage']

export type AssistantSendInput = {
  message: string
  context: AssistantContextItem[]
  sessionId: string | null
  clientTurnId: string
  entityId?: string
  tier?: AssistantTier
  image?: AssistantImage
}

type Schemas = components['schemas']

export type ExternalMcpControl = Schemas['ExternalMcpControlResponse']

const eventNames = [
  'session',
  'message',
  'pending-proposal',
  'capability-error',
  'ui-command',
  'file',
  'error',
  'done',
]

const endsStream = (event: AssistantEvent) =>
  event.type === 'done' || (event.type === 'error' && event.code === 'AI_UNAVAILABLE')

export async function streamAssistant(
  input: AssistantSendInput,
  onEvent: (event: AssistantEvent) => void,
  signal?: AbortSignal,
) {
  const response = await send('/api/v1/ai/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(input),
    signal,
  })
  if (!response.body) throw new Error('Assistant stream returned no body')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let completed = false
  const emit = (event: AssistantEvent) => {
    if (endsStream(event)) completed = true
    onEvent(event)
  }
  let buffer = ''
  while (true) {
    const { value, done } = await reader.read()
    buffer += decoder.decode(value, { stream: !done })
    let boundary = /\r\n\r\n|\n\n|\r\r/.exec(buffer)
    while (boundary?.index != null) {
      emitBlock(buffer.slice(0, boundary.index), emit)
      buffer = buffer.slice(boundary.index + boundary[0].length)
      boundary = /\r\n\r\n|\n\n|\r\r/.exec(buffer)
    }
    if (done) break
  }
  if (buffer.trim()) emitBlock(buffer, emit)
  if (!completed) onEvent({ type: 'error', code: 'AI_STREAM_INCOMPLETE' })
}

export async function getSession(sessionId: string) {
  return request(
    `/api/v1/ai/sessions/${encodeURIComponent(sessionId)}`,
    decodeSession,
  )
}

export async function getExternalMcpControl() {
  return request('/api/v1/mcp/control', decodeExternalMcpControl)
}

export async function getAssistantAvailability(entityId: string) {
  return request(
    `/api/v1/entities/${encodeURIComponent(entityId)}/ai/availability`,
    (raw) => ({
      available: requireBoolean(record(raw, 'assistant availability').available, 'available'),
    }),
  )
}

export async function deleteSession(sessionId: string) {
  return requestNoContent(`/api/v1/ai/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
  })
}

export async function approveProposal(id: string) {
  return request(
    `/api/v1/ai/proposals/${encodeURIComponent(id)}/approve`,
    decodeApproval,
    { method: 'POST' },
  )
}

export async function rejectProposal(id: string) {
  return requestNoContent(`/api/v1/ai/proposals/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

type AssistantApproval = { result: unknown }

function decodeApproval(raw: unknown): AssistantApproval {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw) || !('result' in raw)) {
    throw new Error('Expected an assistant approval result')
  }
  return { result: raw.result } satisfies AssistantApproval
}

function decodeExternalMcpControl(raw: unknown): ExternalMcpControl {
  const control = record(raw, 'external MCP control')
  return {
    connectorUrl: requireString(control.connectorUrl, 'connectorUrl'),
  } satisfies ExternalMcpControl
}

function record(value: unknown, what: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`Expected ${what}`)
  }
  return value as Record<string, unknown>
}

function decodeSession(raw: unknown): AssistantSession {
  const session = record(raw, 'an assistant session')
  if (!Array.isArray(session.turns)) throw new Error('Expected assistant session turns')
  return {
    sessionId: requireString(session.sessionId, 'sessionId'),
    turns: session.turns.map(decodeStoredTurn),
    activeTurn: session.activeTurn === true,
  } satisfies AssistantSession
}

function decodeStoredTurn(raw: unknown): AssistantStoredTurn {
  const turn = record(raw, 'an assistant turn')
  return {
    userText: requireString(turn.userText, 'userText'),
    assistantText: requireString(turn.assistantText, 'assistantText'),
    capabilityErrors: Array.isArray(turn.capabilityErrors)
      ? turn.capabilityErrors.map(decodeCapabilityError)
      : [],
    proposals: Array.isArray(turn.proposals) ? turn.proposals.map(decodeStoredProposal) : [],
    errorCode: typeof turn.errorCode === 'string' ? turn.errorCode : null,
  } satisfies AssistantStoredTurn
}

function decodeCapabilityError(raw: unknown): AssistantCapabilityError {
  const failure = record(raw, 'a capability error')
  return {
    tool: requireString(failure.tool, 'tool'),
    reason: requireString(failure.reason, 'reason'),
  } satisfies AssistantCapabilityError
}

function decodeStoredProposal(raw: unknown): AssistantStoredProposal {
  const proposal = record(raw, 'a stored proposal')
  return {
    id: requireString(proposal.id, 'id'),
    details: proposal.details,
    expiresAt: requireString(proposal.expiresAt, 'expiresAt'),
    outcome: requireString(proposal.outcome, 'outcome') as AssistantProposalOutcome,
  } satisfies AssistantStoredProposal
}

function emitBlock(block: string, onEvent: (event: AssistantEvent) => void) {
  let name = ''
  const data: string[] = []
  for (const line of block.split(/\r\n|\r|\n/)) {
    if (line.startsWith('event:')) name = line.slice(6).trim()
    if (line.startsWith('data:')) data.push(line.slice(5).trimStart())
  }
  if (!eventNames.includes(name)) return
  let parsed: unknown
  try {
    parsed = JSON.parse(data.join('\n'))
  } catch {
    onEvent({ type: 'error', code: 'AI_STREAM_UNREADABLE' })
    return
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    onEvent({ type: 'error', code: 'AI_STREAM_UNREADABLE' })
    return
  }
  const payload = parsed as Record<string, unknown>
  if (name === 'session' && typeof payload.sessionId === 'string') {
    onEvent({ type: 'session', sessionId: payload.sessionId })
  } else if (name === 'message' && typeof payload.text === 'string') {
    onEvent({ type: 'message', text: payload.text })
  } else if (
    name === 'pending-proposal'
    && typeof payload.id === 'string'
    && typeof payload.expiresAt === 'string'
  ) {
    onEvent({
      type: 'pending-proposal',
      id: payload.id,
      details: payload.details,
      expiresAt: payload.expiresAt,
    })
  } else if (
    name === 'capability-error'
    && typeof payload.tool === 'string'
    && typeof payload.reason === 'string'
  ) {
    onEvent({ type: 'capability-error', tool: payload.tool, reason: payload.reason })
  } else if (name === 'ui-command') {
    const command = decodeAssistantBrowserCommand(payload)
    if (command) onEvent({ type: 'ui-command', command })
    else onEvent({ type: 'error', code: 'AI_UI_COMMAND_UNSUPPORTED' })
  } else if (
    name === 'file'
    && typeof payload.name === 'string'
    && typeof payload.contentType === 'string'
    && typeof payload.content === 'string'
  ) {
    onEvent({
      type: 'file',
      name: payload.name,
      contentType: payload.contentType,
      content: payload.content,
    })
  } else if (name === 'error' && typeof payload.code === 'string') {
    onEvent({ type: 'error', code: payload.code })
  } else if (name === 'done') {
    onEvent({ type: 'done' })
  } else {
    onEvent({
      type: 'error',
      code: name === 'ui-command' ? 'AI_UI_COMMAND_UNSUPPORTED' : 'AI_STREAM_UNREADABLE',
    })
  }
}
